// Field service set-up and its module-wide switches (PRD 88 §4.2, §6).
//
//   GET   /api/v1/business/field/settings          every books role and the dispatcher
//   POST  /api/v1/business/field/settings/set-up   owner; 422 time_zone_required
//   PATCH /api/v1/business/field/settings          owner; lock_version, 409 stale
//
// Called by the Overview (its checklist), the Settings page, and
// BusinessContext (the set-up state the sidebar and the Administration tile
// read through businessModulesStore).
//
// EVERY WRITE PARSES ITS RESPONSE. apiFetch never throws on a status, so a
// write that dropped the Response would turn 403 module_required, 409 stale
// and 422 time_zone_required into a success. parseData throws BusinessApiError
// with the server's code and reason instead.

import { businessApiGet, businessApiPatch, businessApiPost } from '../utils/apiUtils';
import { BusinessApiError, parseData } from './businessApiCommon';
import {
  FieldServiceSettingsPayload,
  FieldServiceSettingsUpdate,
  FieldServiceSetUpParams,
} from '../types/fieldServiceApi';

const SETTINGS_PATH = '/api/v1/business/field/settings';

/** A write to a business that holds the module but has never set it up (FieldService::Scoped). */
export const FIELD_SERVICE_NOT_SET_UP_CODE = 'field_service_not_set_up';

/** Set-up refused because the business has never chosen a time zone (ruling R3). */
export const TIME_ZONE_REQUIRED_CODE = 'time_zone_required';

/** A settings save made against an older lock_version. */
export const STALE_CODE = 'stale';

/** The module gate's refusal: the business does not hold field service. */
export const MODULE_REQUIRED_CODE = 'module_required';

export const fieldServiceSettingsService = {
  /** Never creates anything: `set_up: false` and no `settings` before set-up. */
  async get(): Promise<FieldServiceSettingsPayload> {
    return parseData<FieldServiceSettingsPayload>(await businessApiGet(SETTINGS_PATH));
  },

  /**
   * The one explicit act that turns a business holding the module into one
   * using it. Idempotent on the api: a second call answers the existing row.
   */
  async setUp(params: FieldServiceSetUpParams): Promise<FieldServiceSettingsPayload> {
    return parseData<FieldServiceSettingsPayload>(await businessApiPost(`${SETTINGS_PATH}/set-up`, params));
  },

  /** Owner only; `lock_version` is the one last read. */
  async update(params: FieldServiceSettingsUpdate): Promise<FieldServiceSettingsPayload> {
    return parseData<FieldServiceSettingsPayload>(await businessApiPatch(SETTINGS_PATH, params));
  },
};

/**
 * The settings as they now are, from a 409 `stale` refusal — the api sends
 * them in `details.current` so the second of two editors sees what the first
 * saved. Null for any other error.
 */
export function staleSettingsOf(error: unknown): FieldServiceSettingsPayload | null {
  if (!(error instanceof BusinessApiError) || error.code !== STALE_CODE) return null;
  const details = error.details as unknown as { current?: FieldServiceSettingsPayload } | null;
  return details?.current ?? null;
}
